/**
 * The app's sounds (FR-SET-02, decision 171): a few short ones made in code with Web Audio, no files,
 * one picked per device. They play only while the app is open; a push that comes while it's closed
 * plays the phone's own sound, since Web Push can't choose one.
 */
export const SOUND_NAMES = ['chirp', 'chime', 'pluck', 'drop', 'thump', 'rise'] as const
export type SoundName = (typeof SOUND_NAMES)[number]
/** Today's beep, so nobody's sound changes until they pick another. */
export const DEFAULT_SOUND: SoundName = 'chirp'

export const SOUND_LABEL: Record<SoundName, { label: string; hint: string }> = {
  chirp: { label: 'Chirp', hint: 'One short beep' },
  chime: { label: 'Chime', hint: 'Two bell notes' },
  pluck: { label: 'Pluck', hint: 'A plucked string' },
  drop: { label: 'Drop', hint: 'A falling drip' },
  thump: { label: 'Thump', hint: 'Two soft, low thumps' },
  rise: { label: 'Rise', hint: 'Two notes going up' },
}

/** A stored name that's no longer one of them (or never was) plays the default. */
export const soundOr = (v: unknown): SoundName =>
  SOUND_NAMES.includes(v as SoundName) ? (v as SoundName) : DEFAULT_SOUND

/** The loudest any one voice gets: today's beep, so they all sit about as gently. */
const PEAK = 0.04

/**
 * A voice's volume over time from `t`: up in `attack`, held, then down to silence by `t + length`.
 * Ramps at both ends so nothing clicks; `ring` decays exponentially instead of a straight fade.
 */
function envelope(g: GainNode, t: number, peak: number, length: number, attack = 0.005, ring = false) {
  const p = g.gain
  p.setValueAtTime(0, t)
  p.linearRampToValueAtTime(peak, t + attack)
  if (ring) {
    p.exponentialRampToValueAtTime(0.0001, t + length)
  } else {
    p.setValueAtTime(peak, t + Math.max(attack, length - 0.012))
    p.linearRampToValueAtTime(0, t + length)
  }
  p.setValueAtTime(0, t + length)
}

/** One oscillator through its own envelope into `out`, started at `t` and stopped once silent. */
function tone(
  ctx: BaseAudioContext,
  out: AudioNode,
  t: number,
  o: { type?: OscillatorType; freq: number; to?: number; peak: number; length: number; ring?: boolean },
) {
  const osc = ctx.createOscillator()
  const g = ctx.createGain()
  osc.type = o.type ?? 'sine'
  osc.frequency.setValueAtTime(o.freq, t)
  if (o.to) osc.frequency.exponentialRampToValueAtTime(o.to, t + o.length)
  envelope(g, t, o.peak, o.length, 0.005, o.ring)
  osc.connect(g).connect(out)
  osc.start(t)
  osc.stop(t + o.length + 0.02)
  return osc
}

/** A short burst of white noise, made once per context. */
const noises = new WeakMap<BaseAudioContext, AudioBuffer>()
function noise(ctx: BaseAudioContext) {
  let b = noises.get(ctx)
  if (!b) {
    b = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * 0.05), ctx.sampleRate)
    const d = b.getChannelData(0)
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1
    noises.set(ctx, b)
  }
  return b
}

/** A thump: a low sine falling in pitch, with a little filtered noise for the knuckle. */
function thump(ctx: BaseAudioContext, out: AudioNode, t: number) {
  tone(ctx, out, t, { freq: 210, to: 110, peak: PEAK * 2.2, length: 0.09, ring: true })
  const src = ctx.createBufferSource()
  src.buffer = noise(ctx)
  const bp = ctx.createBiquadFilter()
  bp.type = 'bandpass'
  bp.frequency.value = 1200
  bp.Q.value = 1.2
  const g = ctx.createGain()
  envelope(g, t, PEAK * 0.6, 0.03, 0.002, true)
  src.connect(bp).connect(g).connect(out)
  src.start(t)
  src.stop(t + 0.05)
}

/** Each sound, scheduled from `t` into `out`. All under 400 ms. */
export const RECIPES: Record<SoundName, (ctx: BaseAudioContext, out: AudioNode, t: number) => void> = {
  // The beep the app has always made: 880 Hz for 80 ms.
  chirp: (ctx, out, t) => tone(ctx, out, t, { freq: 880, peak: PEAK, length: 0.08 }),
  // A bell: two notes a fifth apart, each with a faint high overtone, ringing out.
  chime: (ctx, out, t) => {
    for (const [at, f] of [
      [0, 1046.5],
      [0.09, 1568],
    ] as const) {
      tone(ctx, out, t + at, { freq: f, peak: PEAK * 0.8, length: 0.3, ring: true })
      tone(ctx, out, t + at, { freq: f * 2.76, peak: PEAK * 0.15, length: 0.12, ring: true })
    }
  },
  // A string: a bright sawtooth whose filter closes as it dies away.
  pluck: (ctx, out, t) => {
    const lp = ctx.createBiquadFilter()
    lp.type = 'lowpass'
    lp.Q.value = 2
    lp.frequency.setValueAtTime(4000, t)
    lp.frequency.exponentialRampToValueAtTime(500, t + 0.2)
    lp.connect(out)
    tone(ctx, lp, t, { type: 'sawtooth', freq: 392, peak: PEAK * 0.7, length: 0.3, ring: true })
  },
  // A drip: a quick sweep down.
  drop: (ctx, out, t) =>
    tone(ctx, out, t, { freq: 1500, to: 450, peak: PEAK * 1.1, length: 0.13, ring: true }),
  thump: (ctx, out, t) => {
    thump(ctx, out, t)
    thump(ctx, out, t + 0.14)
  },
  // Two soft triangle notes, the second higher.
  rise: (ctx, out, t) => {
    tone(ctx, out, t, { type: 'triangle', freq: 660, peak: PEAK, length: 0.09 })
    tone(ctx, out, t + 0.11, { type: 'triangle', freq: 990, peak: PEAK, length: 0.11 })
  },
}

/** One context for the app, made the first time it's needed. */
let ctx: AudioContext | null = null
function context() {
  if (!ctx && typeof AudioContext !== 'undefined') {
    // Mixes with whatever else is playing, and the silent switch hushes it (Safari 16.4+).
    const session = (navigator as { audioSession?: { type: string } }).audioSession
    if (session) session.type = 'ambient'
    ctx = new AudioContext()
  }
  return ctx
}

/** Play one of the sounds now. Quietly nothing if the device has no Web Audio or it's still locked. */
export function play(name: SoundName) {
  try {
    const c = context()
    if (!c) return
    const go = () => {
      try {
        RECIPES[soundOr(name)](c, c.destination, c.currentTime + 0.01)
      } catch {}
    }
    if (c.state === 'running') return go()
    // Locked until a tap (iOS, and Chrome before any): ask, and play only if it opens right away, so a
    // sound never comes late.
    const asked = Date.now()
    c.resume().then(
      () => Date.now() - asked < 300 && go(),
      () => {},
    )
  } catch {}
}

// Browsers only let sound start from a tap or a key: make or wake the context on each until it runs,
// so the first message after you've touched the app can play (and after iOS suspends it again).
if (typeof window !== 'undefined') {
  const wake = () => {
    try {
      const c = context()
      if (c && c.state !== 'running') c.resume().catch(() => {})
    } catch {}
  }
  for (const e of ['pointerdown', 'keydown'])
    window.addEventListener(e, wake, { capture: true, passive: true })
}
